import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Drawer,
    Group,
    Progress,
    ScrollArea,
    Stack,
    Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { User } from '../../utils/session';
import { Account } from '../AccountCard';
import { SyncRuleFormValues } from '../sync_rule/interface';
import { getInitialSyncRuleFormValues } from '../sync_rule/formDefaults';
import { OnboardingTemplateKey } from '../sync_rule/templates';
import {
    clearOnboardingState,
    DestinationMode,
    readOnboardingState,
    resolveResumeStep,
    setOnboardingDismissed,
    writeOnboardingState,
} from './state';
import { OnboardingConnectAccount } from './OnboardingConnectAccount';
import { OnboardingSourceCalendarSelect } from './OnboardingSourceCalendarSelect';
import { OnboardingDestinationSelect } from './OnboardingDestinationSelect';
import { OnboardingTemplateChoiceStep } from './OnboardingTemplateChoice';
import { OnboardingPrefixSuffix } from './OnboardingPrefixSuffix';
import { OnboardingAdvancedSettings } from './OnboardingAdvancedSettings';
import { OnboardingReviewCreate } from './OnboardingReviewCreate';
import { OnboardingSuccess } from './OnboardingSuccess';


interface OnboardingDrawerProps {
    opened: boolean;
    onClose: () => void;
    accounts: Account[];
    user: User;
    updateAccounts: () => void;
    onComplete: () => void;
    onGoToSyncs: () => void;
}

export const OnboardingDrawer: React.FC<OnboardingDrawerProps> = ({
    opened,
    onClose,
    accounts,
    user,
    updateAccounts,
    onComplete,
    onGoToSyncs,
}) => {
    const { t } = useTranslation(['app']);
    const [activeStep, setActiveStep] = useState(0);
    const [destinationMode, setDestinationMode] = useState<DestinationMode>(null);
    const [templateChoice, setTemplateChoice] = useState<OnboardingTemplateKey | null>(null);
    const [completed, setCompleted] = useState(false);

    const defaultTitle = t('dashboard.sync.preview.example-title');
    const defaultDescription = t('dashboard.sync.preview.example-description');
    const [, setDestinationTitle] = useState(defaultTitle);
    const [, setDestinationDescription] = useState(defaultDescription);

    const form = useForm<SyncRuleFormValues>({
        initialValues: getInitialSyncRuleFormValues(null),
        validate: {
            source: (value) => (!value ? t('dashboard.required-field') : null),
            destination: (value, values) =>
                !value ? t('dashboard.required-field') :
                    value === values.source ? t('dashboard.sync.different-calendars') : null,
        },
    });

    useEffect(() => {
        if (!opened) {
            return;
        }
        const saved = readOnboardingState();
        const step = resolveResumeStep(accounts.length, saved);
        setActiveStep(step);
        if (saved?.active && step !== saved.step) {
            writeOnboardingState({
                ...saved,
                step,
            });
        }
        if (saved?.sourceCalendarUuid) {
            form.setFieldValue('source', saved.sourceCalendarUuid);
        }
        if (saved?.destinationMode) {
            setDestinationMode(saved.destinationMode);
        }
    }, [opened, accounts.length]);

    useEffect(() => {
        if (destinationMode === 'connect_another' && activeStep === 2) {
            writeOnboardingState({
                active: true,
                step: 2,
                sourceCalendarUuid: form.values.source || null,
                destinationMode: 'connect_another',
            });
        }
    }, [destinationMode, activeStep, form.values.source]);

    const persistState = (step: number) => {
        writeOnboardingState({
            active: true,
            step,
            sourceCalendarUuid: form.values.source || null,
            destinationMode,
        });
    };

    const handleExit = () => {
        clearOnboardingState();
        setOnboardingDismissed(user.uuid);
        onClose();
    };

    const handleComplete = () => {
        clearOnboardingState();
        setOnboardingDismissed(user.uuid);
        setCompleted(true);
        onComplete();
    };

    const canGoNext = (): boolean => {
        switch (activeStep) {
            case 0:
                return accounts.length >= 1;
            case 1:
                return !!form.values.source;
            case 2:
                return !!form.values.destination && form.values.destination !== form.values.source;
            case 3:
                return templateChoice !== null;
            case 4:
                return true;
            case 5:
                return true;
            case 6:
                return false;
            default:
                return false;
        }
    };

    const getNextStep = (current: number): number => {
        if (current === 3 && templateChoice === 'busy') {
            return 5;
        }
        return current + 1;
    };

    const getPrevStep = (current: number): number => {
        if (current === 5 && templateChoice === 'busy') {
            return 3;
        }
        return current - 1;
    };

    const handleNext = () => {
        if (activeStep === 1) {
            persistState(2);
        } else if (activeStep === 2) {
            persistState(3);
        }
        setActiveStep(getNextStep(activeStep));
    };

    const handleBack = () => {
        setActiveStep(getPrevStep(activeStep));
    };

    const handleSkipPrefix = () => {
        form.setFieldValue('summary', '%original%');
        form.setFieldValue('description', '%original%');
        setDestinationTitle(defaultTitle);
        setDestinationDescription(defaultDescription);
        setActiveStep(5);
    };

    const stepLabels = [
        t('dashboard.onboarding.steps.connect'),
        t('dashboard.onboarding.steps.source'),
        t('dashboard.onboarding.steps.destination'),
        t('dashboard.onboarding.steps.template'),
        t('dashboard.onboarding.steps.prefix'),
        t('dashboard.onboarding.steps.settings'),
        t('dashboard.onboarding.steps.create'),
    ];

    const progressPercent = Math.round(((activeStep + 1) / stepLabels.length) * 100);

    const renderStep = () => {
        if (completed) {
            return <OnboardingSuccess onGoToSyncs={() => { onGoToSyncs(); handleExit(); }} />;
        }

        switch (activeStep) {
            case 0:
                return (
                    <OnboardingConnectAccount
                        user={user}
                        accounts={accounts}
                        updateAccounts={updateAccounts}
                    />
                );
            case 1:
                return <OnboardingSourceCalendarSelect accounts={accounts} form={form} />;
            case 2:
                return (
                    <OnboardingDestinationSelect
                        accounts={accounts}
                        user={user}
                        form={form}
                        destinationMode={destinationMode}
                        onDestinationModeChange={setDestinationMode}
                        updateAccounts={updateAccounts}
                    />
                );
            case 3:
                return (
                    <OnboardingTemplateChoiceStep
                        form={form}
                        templateChoice={templateChoice}
                        onTemplateChoice={setTemplateChoice}
                        setDestinationTitle={setDestinationTitle}
                        setDestinationDescription={setDestinationDescription}
                        defaultTitle={defaultTitle}
                        defaultDescription={defaultDescription}
                    />
                );
            case 4:
                return (
                    <OnboardingPrefixSuffix
                        form={form}
                        setDestinationTitle={setDestinationTitle}
                        setDestinationDescription={setDestinationDescription}
                        defaultTitle={defaultTitle}
                        defaultDescription={defaultDescription}
                        onSkip={handleSkipPrefix}
                    />
                );
            case 5:
                return <OnboardingAdvancedSettings form={form} accounts={accounts} />;
            case 6:
                return (
                    <OnboardingReviewCreate
                        form={form}
                        accounts={accounts}
                        templateChoice={templateChoice}
                        onSuccess={handleComplete}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Drawer
            opened={opened}
            onClose={handleExit}
            size="100%"
            position="right"
            withCloseButton={false}
            closeOnClickOutside={false}
            closeOnEscape={false}
            padding={0}
            title={null}
            styles={{
                content: { height: '100dvh' },
                body: { padding: 0, height: '100%', overflow: 'hidden' },
            }}
        >
            <Stack
                h="100%"
                gap={0}
                data-onboarding-drawer
                style={{ overflow: 'hidden' }}
            >
                <Box
                    px={{ base: 'sm', sm: 'md' }}
                    py="xs"
                    style={{
                        flexShrink: 0,
                        borderBottom: '1px solid var(--mantine-color-gray-3)',
                    }}
                >
                    <Group justify="space-between" mb="xs" wrap="nowrap">
                        <Text fw={600} size="lg">{t('dashboard.onboarding.title')}</Text>
                        <Button variant="outline" color="gray" size="sm" onClick={handleExit}>
                            {t('dashboard.onboarding.exit')}
                        </Button>
                    </Group>
                    <Stack gap="xs">
                        <Group gap="sm" align="center" wrap="nowrap">
                            <Progress
                                flex={1}
                                value={progressPercent}
                                size="md"
                                aria-label={stepLabels[activeStep]}
                            />
                            <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                                {progressPercent}%
                            </Text>
                        </Group>
                        <Text size="sm" fw={600} ta="center">
                            {stepLabels[activeStep]}
                        </Text>
                    </Stack>
                </Box>

                <ScrollArea flex={1} style={{ minHeight: 0 }}>
                    <Box
                        px={{ base: 'sm', sm: 'md' }}
                        py={{ base: 'sm', sm: 'md' }}
                        pb={{ base: 'xl', sm: 'md' }}
                        maw={600}
                        mx="auto"
                    >
                        {renderStep()}
                    </Box>
                </ScrollArea>

                {!completed && activeStep < 6 && (
                    <Group
                        justify="space-between"
                        px={{ base: 'sm', sm: 'md' }}
                        py="sm"
                        style={{
                            flexShrink: 0,
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 200,
                            borderTop: '1px solid var(--mantine-color-gray-3)',
                            backgroundColor: 'var(--mantine-color-body)',
                            paddingBottom: 'max(var(--mantine-spacing-sm), env(safe-area-inset-bottom, 0px))',
                        }}
                    >
                        <Button variant="default" onClick={handleBack} disabled={activeStep === 0}>
                            {t('dashboard.onboarding.back')}
                        </Button>
                        <Button onClick={handleNext} disabled={!canGoNext()}>
                            {t('dashboard.onboarding.next')}
                        </Button>
                    </Group>
                )}
            </Stack>
        </Drawer>
    );
};
